/**
 * Leagues (docs/game-design.md §7–8, L1–L8): Hattrick-style pyramids per country, 15-day seasons
 * with a league fight every day.
 */
import type { BotDifficulty } from './bot';

export type DivisionName = 'legend' | 'champion' | 'pro' | 'contender' | 'amateur' | 'rookie';

export const LEAGUES = {
  /** Days 1–14 are a double round robin, day 15 the qualifiers; no break day (L2). */
  seasonDays: 15,
  roundRobinDays: 14,
  qualifierDay: 15,
  /** Fighters per series. */
  seriesSize: 8,
  /**
   * The first day of season 1, as a calendar date in each country's own time zone. Seasons follow
   * each other without a break, so every country is on the same day of the same season.
   */
  firstSeasonStart: '2026-10-04',
  /** Division names from the top (I Legend … V Amateur); VI and below are all Rookie (L1). */
  divisionNames: ['legend', 'champion', 'pro', 'contender', 'amateur', 'rookie'] as readonly DivisionName[],
  /** League fight time, in the country's (its capital's) time zone. */
  fightHour: 20, // placeholder
  fightMinute: 0, // placeholder
  /** The live window that opens at fight time (L3). */
  windowMinutes: 10, // placeholder
  /** Table points (L4). */
  points: { win: 3, draw: 1, loss: 0 },
  /**
   * The next division opens at the start of a season when every series of the lowest division
   * has at least this many humans (§7.2).
   */
  openNextAtHumans: 6, // placeholder
  /** New fighters fill the series with the fewest humans first, until each has this many (§7.7). */
  joinEvenUpTo: 4,
  /** A fighter who hasn't played for this many days loses their place at the next checkpoint (L7). */
  inactiveDays: 14, // placeholder
  /**
   * Inactivity checkpoints, as the day whose fight comes next: the season start (day 1), after
   * day 7 and after day 14 (§7.8).
   */
  checkpointDays: [1, 8, 15] as readonly number[],
  /** League bonus points at the season end (§4.2, G6), only in series with `minHumans` humans or more. */
  bonusPoints: { promoted: 1, seriesWinner: 1, nationalChampion: 3, minHumans: 4 },
  /** Coins at the season end (§7.9): not decided yet, so none. */
  coins: { promoted: 0, seriesWinner: 0, nationalChampion: 0 }, // placeholder
  bots: {
    /** How the bots in league places fight. */
    difficulty: 'normal' as BotDifficulty, // placeholder
    /** A league bot's skill points: this many in the lowest open division… */
    pointsLowest: 10, // placeholder
    /** …and this many more for each division above it. */
    pointsPerDivision: 10, // placeholder
  },
  /** The bot that fights an absent fighter's game plan (L3, §8). */
  planBotDifficulty: 'normal' as BotDifficulty, // placeholder
} as const;
